import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { buildFilesUrl } from '../config/api';

interface FileItem {
  key: string;
  size: number;
  uploaded: string;
  metadata: {
    filename?: string;
    contentType?: string;
    isPublic?: string | boolean;
    userId?: string;
    uploadedAt?: string;
  };
}

interface FileManagerProps {
  onNarrative?: (message: string, type: 'info' | 'success' | 'error') => void;
  refreshTrigger?: number;
}

export const FileManager: React.FC<FileManagerProps> = ({ onNarrative, refreshTrigger }) => {
  const { token } = useAuth();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const FILES_URL = buildFilesUrl('');

  // Load user files
  const loadFiles = async () => {
    if (!token) return;

    setLoading(true);
    onNarrative?.('📂 Loading your files from R2...', 'info');

    try {
      const response = await fetch(`${FILES_URL}/files`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load files');
      }

      const data = await response.json();
      setFiles(data.files || []);
      onNarrative?.(`✅ Loaded ${data.count} files from R2 storage`, 'success');
    } catch (error) {
      onNarrative?.(
        `❌ Failed to load files: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [token, refreshTrigger]);

  const handleDelete = async (key: string) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    onNarrative?.(`🗑️ Deleting file: ${key}`, 'info');

    try {
      const response = await fetch(`${FILES_URL}/files/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      onNarrative?.('✅ File deleted successfully!', 'success');
      loadFiles(); // Reload the list
    } catch (error) {
      onNarrative?.(
        `❌ Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    }
  };

  const handleDownload = (file: FileItem) => {
    const url = `${FILES_URL}/files/${encodeURIComponent(file.key)}`;
    const isPublic = file.metadata?.isPublic === true || file.metadata?.isPublic === 'true';
    
    if (isPublic) {
      // Public files can be downloaded directly
      window.open(url, '_blank');
      onNarrative?.(`📥 Downloading public file: ${file.metadata?.filename || file.key}`, 'info');
    } else {
      // Private files need auth header, so we'll fetch and create blob
      onNarrative?.(`🔐 Downloading private file: ${file.metadata?.filename || file.key}`, 'info');
      
      fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
        .then(res => res.blob())
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = file.metadata?.filename || 'download';
          a.click();
          window.URL.revokeObjectURL(blobUrl);
          onNarrative?.('✅ Download complete!', 'success');
        })
        .catch(error => {
          onNarrative?.(`❌ Download failed: ${error.message}`, 'error');
        });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
      setShowUploadModal(true);
    }
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0 || !token) return;

    setIsUploading(true);
    setUploadProgress(0);
    let successCount = 0;

    onNarrative?.(`📤 Uploading ${selectedFiles.length} file(s)...`, 'info');

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${FILES_URL}/files`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        successCount++;
        setUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
      } catch (error) {
        onNarrative?.(
          `❌ Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`,
          'error'
        );
      }
    }

    setIsUploading(false);
    setShowUploadModal(false);
    setSelectedFiles([]);

    if (successCount === selectedFiles.length) {
      onNarrative?.(`✅ Successfully uploaded all ${successCount} files!`, 'success');
    } else {
      onNarrative?.(`⚠️ Uploaded ${successCount}/${selectedFiles.length} files`, 'error');
    }

    loadFiles();
  };

  const getTotalSize = () => {
    return files.reduce((acc, file) => acc + file.size, 0);
  };

  const getFileTypeStats = () => {
    const stats: Record<string, number> = {};
    files.forEach(file => {
      const type = file.metadata?.contentType?.split('/')[0] || 'other';
      stats[type] = (stats[type] || 0) + 1;
    });
    return stats;
  };

  return (
    <div className="space-y-6">
      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="text-3xl mb-2">📁</div>
          <div className="text-2xl font-bold">{files.length}</div>
          <div className="text-sm opacity-90">Total Files</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="text-3xl mb-2">💾</div>
          <div className="text-2xl font-bold">{formatFileSize(getTotalSize())}</div>
          <div className="text-sm opacity-90">Storage Used</div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="text-3xl mb-2">🖼️</div>
          <div className="text-2xl font-bold">{getFileTypeStats()['image'] || 0}</div>
          <div className="text-sm opacity-90">Images</div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="text-3xl mb-2">📄</div>
          <div className="text-2xl font-bold">{(getFileTypeStats()['application'] || 0) + (getFileTypeStats()['text'] || 0)}</div>
          <div className="text-sm opacity-90">Documents</div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">📁 R2 File Manager</h2>
          <div className="flex gap-2">
            <button
              onClick={() => document.getElementById('file-input')?.click()}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded transition-colors flex items-center gap-2"
            >
              <span>📤</span>
              <span>Upload Files</span>
            </button>
            <button
              onClick={loadFiles}
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
            >
              {loading ? '⌛ Loading...' : '🔄 Refresh'}
            </button>
          </div>
          <input
            id="file-input"
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Drag & Drop Zone */}
        {files.length === 0 && (
          <div
            className={`border-3 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer mb-6 ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
            }`}
            onClick={() => document.getElementById('file-input')?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const droppedFiles = Array.from(e.dataTransfer.files);
              if (droppedFiles.length > 0) {
                setSelectedFiles(droppedFiles);
                setShowUploadModal(true);
              }
            }}
          >
            <div className="text-6xl mb-4">📁</div>
            <p className="text-xl font-semibold text-gray-800 mb-2">
              {isDragging ? 'Drop files here!' : 'Drag & drop files or click to browse'}
            </p>
            <p className="text-gray-600">Support for images, documents, videos, and more</p>
            <p className="text-sm text-gray-500 mt-2">Maximum file size: 100MB</p>
          </div>
        )}
      </div>

      {files.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg">No files uploaded yet</p>
          <p className="text-sm mt-2">Upload files using the R2 File Upload panel</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Access
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploaded
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {files.map((file) => (
                <tr 
                  key={file.key}
                  className={`hover:bg-gray-50 cursor-pointer ${
                    selectedFile === file.key ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => {
                    setSelectedFile(file.key);
                    if (file.metadata?.contentType?.startsWith('image/')) {
                      setPreviewFile(file);
                      onNarrative?.('🖼️ Opening image preview...', 'info');
                    }
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="mr-2">
                        {file.metadata?.contentType?.startsWith('image/') ? '🖼️' : '📄'}
                      </span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {file.metadata?.filename || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {file.key}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatFileSize(file.size)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {file.metadata?.contentType || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      (file.metadata?.isPublic === true || file.metadata?.isPublic === 'true')
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {(file.metadata?.isPublic === true || file.metadata?.isPublic === 'true') ? '🌐 Public' : '🔒 Private'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(file.uploaded)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(file);
                      }}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      📥 Download
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(file.key);
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewFile && previewFile.metadata?.contentType?.startsWith('image/') && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewFile(null)}
        >
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">{previewFile.metadata.filename}</h3>
              <button
                onClick={() => setPreviewFile(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              {(previewFile.metadata?.isPublic === true || previewFile.metadata?.isPublic === 'true') ? (
                <img
                  src={`${FILES_URL}/files/${encodeURIComponent(previewFile.key)}`}
                  alt={previewFile.metadata.filename}
                  className="max-w-full max-h-[70vh] object-contain"
                  onError={(e) => {
                    onNarrative?.('❌ Failed to load image preview', 'error');
                    setPreviewFile(null);
                  }}
                />
              ) : (
                <div className="text-center p-8">
                  <div className="mb-6">
                    <span className="text-6xl">🔒</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Private File Protection
                  </h3>
                  <p className="text-gray-600 mb-6">
                    This file is private and requires authentication to view. 
                    Private files are protected from direct browser access for security.
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(previewFile);
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      📥 Download File
                    </button>
                    <p className="text-sm text-gray-500">
                      File: {previewFile.metadata?.filename || 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-400">
                      Size: {formatFileSize(previewFile.size)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
        <p>💡 Files are stored in CloudFlare R2 with automatic global replication</p>
        <p className="mt-1">🔐 Private files require authentication to access</p>
        <p className="mt-1">🖼️ Click on image files to preview</p>
        <p className="mt-1">📤 Drag & drop or paste files to upload</p>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold">Upload Files</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFiles([]);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
              {selectedFiles.map((file, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">
                      {file.type.startsWith('image/') ? '🖼️' :
                       file.type.startsWith('video/') ? '🎥' :
                       file.type.includes('pdf') ? '📕' : '📄'}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">{formatFileSize(file.size)} • {file.type}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mb-6">
              <p className="text-gray-600">
                {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Total size: {formatFileSize(selectedFiles.reduce((acc, f) => acc + f.size, 0))}
              </p>
            </div>

            {isUploading && (
              <div className="mb-6">
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-green-500 h-4 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-center text-sm text-gray-600 mt-2">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={uploadFiles}
                disabled={isUploading}
                className="flex-1 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white px-6 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⏳</span>
                    Uploading...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <span>📤</span>
                    Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
                  </span>
                )}
              </button>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFiles([]);
                }}
                disabled={isUploading}
                className="px-6 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};